// src/components/Chat/ChatInterface.tsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  Typography, 
  Avatar, 
  Paper
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatService } from '../../api/chatService';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  isStreaming?: boolean;
}

interface ChatInterfaceProps {
  fullPage?: boolean;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ fullPage = false }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [messages, isLoading]);

  const resetConversation = () => {
    setMessages([]);
    setSessionId('');
    setInputText('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const queryText = inputText.trim();
    setInputText('');
    setIsLoading(true);

    // Crear mensaje del bot con texto vacío para ir llenándolo con streaming
    const botMessageId = (Date.now() + 1).toString();
    const botMessage: Message = {
      id: botMessageId,
      text: '',
      isUser: false,
      timestamp: new Date(),
      isStreaming: true,
    };
    
    setMessages(prev => [...prev, botMessage]);

    try {
      await chatService.sendMessageStream(
        queryText,
        sessionId,
        // onToken: ir actualizando el mensaje con cada token
        (token: string) => {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === botMessageId 
                ? { ...msg, text: msg.text + token }
                : msg
            )
          );
        },
        // onSession: guardar el session_id si es nuevo
        (newSessionId: string) => {
          if (!sessionId) {
            setSessionId(newSessionId);
          }
        },
        // onComplete: marcar como completado
        () => {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === botMessageId 
                ? { ...msg, isStreaming: false }
                : msg
            )
          );
          setIsLoading(false);
        },
        // onError: mostrar mensaje de error
        (error: Error) => {
          console.error('Error sending message:', error);
          setMessages(prev => 
            prev.map(msg => 
              msg.id === botMessageId 
                ? { 
                    ...msg, 
                    text: 'Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo.',
                    isStreaming: false 
                  }
                : msg
            )
          );
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => 
        prev.map(msg => 
          msg.id === botMessageId 
            ? { 
                ...msg, 
                text: 'Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo.',
                isStreaming: false 
              }
            : msg
        )
      );
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        width: fullPage ? '100%' : 'auto',
        height: fullPage ? '100vh' : { xs: 'calc(100vh - 200px)', md: 600 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: fullPage ? 0 : 3,
        overflow: 'hidden',
        border: fullPage ? 'none' : '1px solid rgba(76, 175, 80, 0.2)',
        boxShadow: fullPage ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.3)',
        position: fullPage ? 'fixed' : 'relative',
        top: fullPage ? 0 : 'auto',
        left: fullPage ? 0 : 'auto',
        right: fullPage ? 0 : 'auto',
        bottom: fullPage ? 0 : 'auto',
      }}
    >
      {/* Header con botón de reiniciar */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: { xs: 1.5, sm: 2, md: 2.5 },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
          boxShadow: '0 2px 10px rgba(76, 175, 80, 0.3)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SmartToyIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
            Asistente Virtual de Enervia
          </Typography>
        </Box>
        <IconButton
          onClick={resetConversation}
          color="primary"
          sx={{ 
            border: '1px solid rgba(255, 255, 255, 0.5)',
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.10)',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255,255,255,0.15)'
            }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Messages Area */}
      <Box 
        sx={{ 
          flex: 1,
          overflow: 'auto',
          p: { xs: 1.5, sm: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          bgcolor: '#1e1e2e',
          minHeight: 0,
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SmartToyIcon sx={{ fontSize: 56, color: '#4CAF50', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              ¡Hola! Soy el asistente de Enervia
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', maxWidth: 400, mx: 'auto' }}>
              Pregúntame sobre nuestros servicios de SAI/UPS, soluciones energéticas o cualquier información de la empresa.
            </Typography>
          </Box>
        )}

        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.isUser ? 'flex-end' : 'flex-start',
              mb: { xs: 0.5, sm: 1 }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 0.75, sm: 1 },
                maxWidth: { xs: '95%', sm: '85%', md: '80%' },
                flexDirection: message.isUser ? 'row-reverse' : 'row'
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  flexShrink: 0,
                  background: message.isUser 
                    ? 'linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)'
                    : 'linear-gradient(45deg, #4CAF50 30%, #81C784 90%)'
                }}
              >
                {message.isUser ? (
                  <PersonIcon sx={{ fontSize: 18 }} />
                ) : (
                  <SmartToyIcon sx={{ fontSize: 18 }} />
                )}
              </Avatar>
              
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: { xs: 2, sm: 3 },
                  background: message.isUser 
                    ? 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)'
                    : '#252537',
                  color: message.isUser ? 'white' : 'text.primary',
                  border: message.isUser ? 'none' : '1px solid rgba(76, 175, 80, 0.2)',
                  boxShadow: message.isUser 
                    ? '0 4px 15px rgba(76, 175, 80, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.2)',
                  wordBreak: 'break-word',
                  minWidth: 0,
                }}
              >
                {message.isUser ? (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.4
                    }}
                  >
                    {message.text}
                  </Typography>
                ) : (
                  <>
                    {message.text ? (
                      <Box
                        sx={{
                          '& p': { margin: '0.5em 0' },
                          '& p:first-of-type': { marginTop: 0 },
                          '& p:last-child': { marginBottom: 0 },
                          '& ul, & ol': { marginLeft: '1.5em', marginTop: '0.5em', marginBottom: '0.5em' },
                          '& li': { marginBottom: '0.25em' },
                          '& code': { 
                            backgroundColor: 'rgba(76, 175, 80, 0.15)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.9em',
                            fontFamily: 'monospace'
                          },
                          '& pre': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            padding: '12px',
                            borderRadius: '8px',
                            overflow: 'auto',
                            marginTop: '0.5em',
                            marginBottom: '0.5em'
                          },
                          '& pre code': {
                            backgroundColor: 'transparent',
                            padding: 0
                          },
                          '& strong': { fontWeight: 700 },
                          '& em': { fontStyle: 'italic' },
                          '& h1, & h2, & h3, & h4, & h5, & h6': {
                            marginTop: '0.75em',
                            marginBottom: '0.5em',
                            fontWeight: 700
                          },
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                        }}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.text}
                        </ReactMarkdown>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#4CAF50',
                            animation: 'pulse 1.4s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '40%': { opacity: 1, transform: 'scale(1)' },
                            },
                          }}
                        />
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#4CAF50',
                            animation: 'pulse 1.4s ease-in-out 0.2s infinite',
                            '@keyframes pulse': {
                              '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '40%': { opacity: 1, transform: 'scale(1)' },
                            },
                          }}
                        />
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#4CAF50',
                            animation: 'pulse 1.4s ease-in-out 0.4s infinite',
                            '@keyframes pulse': {
                              '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '40%': { opacity: 1, transform: 'scale(1)' },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    mt: 0.5,
                    opacity: 0.7,
                    fontSize: '0.65rem'
                  }}
                >
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
                {/* Enlace de contacto solo en mensajes del bot */}
                {!message.isUser && message.text && !message.isStreaming && (
                  <Typography
                    component="a"
                    href="https://enervia.net/contactar/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: '#4CAF50',
                      fontSize: '0.75rem',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#81C784',
                      }
                    }}
                  >
                    ¿Necesitas ayuda personalizada? Contáctanos →
                  </Typography>
                )}
              </Paper>
            </Box>
          </Box>
        ))}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box 
        sx={{ 
          p: { xs: 1.5, sm: 2, md: 2.5 }, 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          bgcolor: '#252537',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 } }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            variant="outlined"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escribe tu pregunta sobre Enervia..."
            disabled={isLoading}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                fontSize: '0.95rem',
              }
            }}
          />
          <IconButton
            onClick={sendMessage}
            disabled={!inputText.trim() || isLoading}
            sx={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                background: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default ChatInterface;